import { useState, useEffect, useCallback, useRef } from 'react';
import { RepoVersionsData, RepoData } from '@app-types/types';
import {
  getRepoVersions,
  addRepoVersion,
  removeRepoVersion,
  getRepoVersionInfo,
  generatePluginFile,
  generateTestJourney,
  testJourney,
  getJourneyScreenshots,
  type ScreenshotInfo,
  getJiraTicket,
  linkJiraTicket,
  triggerJenkinsBuild,
  createSaasPr,
  createSaasBranch,
  planSaasBranch,
  executeSaasBranchStep,
  planOpenAmBranch,
  executeOpenAmBranchStep,
  planEnvironment,
  executeEnvironmentStep,
  type PlannedStep,
  type SaasBranchPlan,
  type OpenAmBranchPlan,
  type EnvironmentPlan,
  createOpenAmPr,
  getPipelineState,
  savePipelineState,
  resetPipelineState,
  type RepoVersionInfo,
  type JiraTicketInfo,
  type JenkinsBuildInfo,
} from '@services/repoVersions';
import {
  getRepoData,
  addPr,
  getPrStatus,
  rerunE2e,
} from '@services/githubRepos';

type StepStatus = 'idle' | 'running' | 'success' | 'error';

interface PipelineStep {
  label: string;
  status: StepStatus;
}

const STEP_COLORS: Record<StepStatus, { bg: string; border: string; text: string; glow: string }> = {
  idle: { bg: '#f3f4f6', border: '#d1d5db', text: '#6b7280', glow: 'none' },
  running: { bg: '#dbeafe', border: '#3b82f6', text: '#1d4ed8', glow: '0 0 8px rgba(59,130,246,0.5)' },
  success: { bg: '#d1fae5', border: '#10b981', text: '#047857', glow: 'none' },
  error: { bg: '#fee2e2', border: '#ef4444', text: '#b91c1c', glow: 'none' },
};

const STEP_ICONS: Record<StepStatus, string> = {
  idle: '○',
  running: '◌',
  success: '✓',
  error: '✕',
};

function initialSteps(): PipelineStep[] {
  return [
    { label: 'Get JIRA Ticket', status: 'idle' },
    { label: 'Run Jenkins Pipeline', status: 'idle' },
    { label: 'Generate Config', status: 'idle' },
    { label: 'Create SaaS Branch', status: 'idle' },
    { label: 'Create SaaS PR', status: 'idle' },
    { label: 'Create OpenAM Branch', status: 'idle' },
    { label: 'Create OpenAM PR', status: 'idle' },
    { label: 'Run E2E', status: 'idle' },
    { label: 'Check E2E', status: 'idle' },
    { label: 'Rerun E2E', status: 'idle' },
    { label: 'Create/Link Env', status: 'idle' },
    { label: 'Create Service Account', status: 'idle' },
    { label: 'Generate Journey', status: 'idle' },
    { label: 'Test Journey', status: 'idle' },
    { label: 'Request Approvals', status: 'idle' },
  ];
}

const RepoVersions: React.FC = () => {
  const [data, setData] = useState<RepoVersionsData>({ repos: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add form
  const [urlInput, setUrlInput] = useState('');
  const [adding, setAdding] = useState(false);

  // Per-repo version state
  const [versions, setVersions] = useState<Record<string, RepoVersionInfo>>({});
  const [versionLoading, setVersionLoading] = useState<Record<string, boolean>>({});

  // Per-repo pipeline state
  const [pipelineSteps, setPipelineSteps] = useState<Record<string, PipelineStep[]>>({});
  const [expandedRepos, setExpandedRepos] = useState<Record<string, boolean>>({});

  // Per-repo linked PR state
  const [linkedPrs, setLinkedPrs] = useState<Record<string, string>>({});
  const [prInputVisible, setPrInputVisible] = useState<Record<string, boolean>>({});
  const [prInputValue, setPrInputValue] = useState<Record<string, string>>({});

  // Per-repo linked branch state (SaaS)
  const [linkedBranches, setLinkedBranches] = useState<Record<string, string>>({});
  const [branchInputVisible, setBranchInputVisible] = useState<Record<string, boolean>>({});
  const [branchInputValue, setBranchInputValue] = useState<Record<string, string>>({});

  // Per-repo linked OpenAM branch state
  const [linkedOpenAmBranches, setLinkedOpenAmBranches] = useState<Record<string, string>>({});
  const [linkedOpenAmPrs, setLinkedOpenAmPrs] = useState<Record<string, string>>({});

  // Per-repo tracked PR ID (for E2E operations on the SaaS repo)
  const [trackedPrIds, setTrackedPrIds] = useState<Record<string, string>>({});

  // Per-repo linked environment state
  const [linkedEnvs, setLinkedEnvs] = useState<Record<string, string>>({});
  const [envInputVisible, setEnvInputVisible] = useState<Record<string, boolean>>({});
  const [envInputValue, setEnvInputValue] = useState<Record<string, string>>({});

  // Per-repo JIRA ticket state
  const [jiraTickets, setJiraTickets] = useState<Record<string, JiraTicketInfo>>({});
  const [jiraInputVisible, setJiraInputVisible] = useState<Record<string, boolean>>({});
  const [jiraInputValue, setJiraInputValue] = useState<Record<string, string>>({});

  // Per-repo Jenkins build state
  const [jenkinsBuilds, setJenkinsBuilds] = useState<Record<string, JenkinsBuildInfo>>({});

  // Per-repo journey screenshots
  const [journeyScreenshots, setJourneyScreenshots] = useState<Record<string, ScreenshotInfo[]>>({});
  const [screenshotModal, setScreenshotModal] = useState<{ repoId: string; index: number } | null>(null);

  // Approval modal for GitHub API calls
  const [approvalModal, setApprovalModal] = useState<{
    repoId: string;
    jiraKey: string;
    plan: SaasBranchPlan | OpenAmBranchPlan | EnvironmentPlan;
    currentStep: number;
    stepResults: Record<string, 'pending' | 'executing' | 'success' | 'skipped' | 'error'>;
    error?: string;
    stepIndex: number; // pipeline step index (3 = SaaS, 5 = OpenAM)
  } | null>(null);

  // Track whether initial state has been loaded from server
  const stateLoaded = useRef(false);

  const getSteps = (repoId: string): PipelineStep[] => pipelineSteps[repoId] || initialSteps();

  const updateStep = (repoId: string, stepIndex: number, status: StepStatus) => {
    setPipelineSteps((prev) => {
      const steps = [...(prev[repoId] || initialSteps())];
      steps[stepIndex] = { ...steps[stepIndex], status };
      return { ...prev, [repoId]: steps };
    });
  };

  // Persist pipeline state to the server
  const persistState = useCallback(async (
    steps: Record<string, PipelineStep[]>,
    prs: Record<string, string>,
    prIds: Record<string, string>,
    envs: Record<string, string>,
    tickets: Record<string, JiraTicketInfo>,
    builds: Record<string, JenkinsBuildInfo>,
    branches: Record<string, string>,
    openAmBranches: Record<string, string>,
    openAmPrs: Record<string, string>,
  ) => {
    try {
      await savePipelineState({
        pipelineSteps: steps,
        linkedPrs: prs,
        trackedPrIds: prIds,
        linkedEnvs: envs,
        jiraTickets: tickets,
        jenkinsBuilds: builds,
        linkedBranches: branches,
        linkedOpenAmBranches: openAmBranches,
        linkedOpenAmPrs: openAmPrs,
      } as any);
    } catch {
      // Silently ignore save errors to avoid disrupting the UI
    }
  }, []);

  // Save state whenever persisted values change (skip initial load)
  useEffect(() => {
    if (!stateLoaded.current) return;
    persistState(pipelineSteps, linkedPrs, trackedPrIds, linkedEnvs, jiraTickets, jenkinsBuilds, linkedBranches, linkedOpenAmBranches, linkedOpenAmPrs);
  }, [pipelineSteps, linkedPrs, trackedPrIds, linkedEnvs, jiraTickets, jenkinsBuilds, linkedBranches, linkedOpenAmBranches, linkedOpenAmPrs, persistState]);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const [result, savedState] = await Promise.all([
        getRepoVersions(),
        getPipelineState().catch(() => null),
      ]);
      setData(result);

      // Restore persisted pipeline state
      if (savedState) {
        const s = savedState as any;
        // Reset any 'running' steps to 'idle' since a page refresh kills in-flight requests
        if (s.pipelineSteps) {
          const fixed: Record<string, { label: string; status: string }[]> = {};
          for (const [id, steps] of Object.entries(s.pipelineSteps as Record<string, { label: string; status: string }[]>)) {
            fixed[id] = steps.map((step) => step.status === 'running' ? { ...step, status: 'idle' } : step);
          }
          setPipelineSteps(fixed);
        }
        if (s.linkedPrs) setLinkedPrs(s.linkedPrs);
        if (s.trackedPrIds) setTrackedPrIds(s.trackedPrIds);
        if (s.linkedEnvs) setLinkedEnvs(s.linkedEnvs);
        if (s.jiraTickets) setJiraTickets(s.jiraTickets);
        if (s.jenkinsBuilds) setJenkinsBuilds(s.jenkinsBuilds);
        if (s.linkedBranches) setLinkedBranches(s.linkedBranches);
        if (s.linkedOpenAmBranches) setLinkedOpenAmBranches(s.linkedOpenAmBranches);
        if (s.linkedOpenAmPrs) setLinkedOpenAmPrs(s.linkedOpenAmPrs);
      }
      stateLoaded.current = true;
    } catch {
      setError('Failed to load repositories');
      stateLoaded.current = true;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchVersion = async (repoId: string) => {
    setVersionLoading((prev) => ({ ...prev, [repoId]: true }));
    try {
      const info = await getRepoVersionInfo(repoId);
      setVersions((prev) => ({ ...prev, [repoId]: info }));
    } catch {
      setVersions((prev) => ({ ...prev, [repoId]: { version: null, dockerfileVersion: null, dockerfileEnvKey: null } }));
    } finally {
      setVersionLoading((prev) => ({ ...prev, [repoId]: false }));
    }
  };

  useEffect(() => {
    (data.repos || []).forEach((repo) => {
      if (versions[repo.id] === undefined && !versionLoading[repo.id]) {
        fetchVersion(repo.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.repos]);

  // Fetch screenshots on load for repos that have run Test Journey
  useEffect(() => {
    if (!stateLoaded.current) return;
    (data.repos || []).forEach((repo) => {
      const steps = pipelineSteps[repo.id];
      const testJourneyStep = steps?.[13];
      if (testJourneyStep && !journeyScreenshots[repo.id]) {
        getJourneyScreenshots(repo.id)
          .then((result) => {
            if (result.screenshots.length > 0) {
              setJourneyScreenshots((prev) => ({ ...prev, [repo.id]: result.screenshots }));
            }
          })
          .catch(() => { /* ignore */ });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.repos, pipelineSteps]);

  // Poll for screenshots while Test Journey is running
  useEffect(() => {
    const runningRepoIds = (data.repos || [])
      .filter((repo) => pipelineSteps[repo.id]?.[13]?.status === 'running')
      .map((repo) => repo.id);

    if (runningRepoIds.length === 0) return;

    const interval = setInterval(() => {
      runningRepoIds.forEach((repoId) => {
        getJourneyScreenshots(repoId)
          .then((result) => {
            if (result.screenshots.length > 0) {
              setJourneyScreenshots((prev) => ({ ...prev, [repoId]: result.screenshots }));
            }
          })
          .catch(() => { /* ignore */ });
      });
    }, 5000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.repos, pipelineSteps]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!urlInput.trim()) return;
    setAdding(true);
    setError(null);
    try {
      const result = await addRepoVersion(urlInput.trim());
      setData(result);
      setUrlInput('');
    } catch {
      setError('Failed to add repository');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (repoId: string) => {
    setError(null);
    try {
      await removeRepoVersion(repoId);
      setData((prev) => ({ ...prev, repos: prev.repos.filter((r) => r.id !== repoId) }));
      setVersions((prev) => {
        const next = { ...prev };
        delete next[repoId];
        return next;
      });
      setPipelineSteps((prev) => {
        const next = { ...prev };
        delete next[repoId];
        return next;
      });
    } catch {
      setError('Failed to remove repository');
    }
  };

  const handleResetPipeline = async (repoId: string) => {
    try {
      await resetPipelineState(repoId);
      setPipelineSteps((prev) => {
        const next = { ...prev };
        delete next[repoId];
        return next;
      });
      setLinkedPrs((prev) => {
        const next = { ...prev };
        delete next[repoId];
        return next;
      });
      setTrackedPrIds((prev) => {
        const next = { ...prev };
        delete next[repoId];
        return next;
      });
      setLinkedEnvs((prev) => {
        const next = { ...prev };
        delete next[repoId];
        return next;
      });
      setJiraTickets((prev) => {
        const next = { ...prev };
        delete next[repoId];
        return next;
      });
      setJenkinsBuilds((prev) => {
        const next = { ...prev };
        delete next[repoId];
        return next;
      });
      setLinkedBranches((prev) => {
        const next = { ...prev };
        delete next[repoId];
        return next;
      });
      setLinkedOpenAmBranches((prev) => {
        const next = { ...prev };
        delete next[repoId];
        return next;
      });
      setLinkedOpenAmPrs((prev) => {
        const next = { ...prev };
        delete next[repoId];
        return next;
      });
      setJourneyScreenshots((prev) => {
        const next = { ...prev };
        delete next[repoId];
        return next;
      });
    } catch {
      setError('Failed to reset pipeline');
    }
  };

  const handleStepClick = async (repoId: string, stepIndex: number) => {
    const steps = getSteps(repoId);
    if (steps[stepIndex].status === 'running') return;

    switch (stepIndex) {
      case 0: {
        // Get JIRA Ticket
        updateStep(repoId, 0, 'running');
        try {
          const ticket = await getJiraTicket(repoId);
          setJiraTickets((prev) => ({ ...prev, [repoId]: ticket }));
          updateStep(repoId, 0, 'success');
        } catch (err: any) {
          updateStep(repoId, 0, 'error');
          const msg = err?.response?.data?.message || 'Failed to get JIRA ticket';
          setError(msg);
        }
        break;
      }
      case 1: {
        // Run Jenkins Pipeline - open Jenkins in new tab (API calls require whitelisting)
        window.open('https://am-jenkins.live.gcp.forgerock.net/view/Partner%20Nodes/', '_blank', 'noopener,noreferrer');
        updateStep(repoId, 1, 'success');
        // Kept for future use when environment is whitelisted:
        // updateStep(repoId, 1, 'running');
        // try {
        //   const build = await triggerJenkinsBuild(repoId);
        //   setJenkinsBuilds((prev) => ({ ...prev, [repoId]: build }));
        //   updateStep(repoId, 1, 'success');
        // } catch (err: any) {
        //   updateStep(repoId, 1, 'error');
        //   const msg = err?.response?.data?.message || 'Failed to trigger Jenkins build';
        //   setError(msg);
        // }
        break;
      }
      case 2: {
        // Generate Config
        updateStep(repoId, 2, 'running');
        try {
          await generatePluginFile(repoId);
          updateStep(repoId, 2, 'success');
        } catch (err: any) {
          updateStep(repoId, 2, 'error');
          const msg = err?.message || err?.response?.data?.message || 'Failed to generate plugin file';
          setError(msg);
        }
        break;
      }
      case 3: {
        // Create SaaS Branch - show approval modal for GitHub API calls
        const ticket = jiraTickets[repoId];
        if (!ticket) {
          setError('Get a JIRA ticket first before creating a SaaS branch');
          break;
        }
        updateStep(repoId, 3, 'running');
        try {
          const plan = await planSaasBranch(repoId, ticket.key);
          const initialResults: Record<string, 'pending' | 'executing' | 'success' | 'skipped' | 'error'> = {};
          plan.steps.forEach((s) => { initialResults[s.id] = s.skip ? 'skipped' : 'pending'; });
          setApprovalModal({
            repoId,
            jiraKey: ticket.key,
            plan,
            currentStep: plan.steps.findIndex((s) => !s.skip),
            stepResults: initialResults,
            stepIndex: 3,
          });
        } catch (err: any) {
          updateStep(repoId, 3, 'error');
          const msg = err?.response?.data?.message || 'Failed to plan SaaS branch';
          setError(msg);
        }
        break;
      }
      case 4: {
        // Create SaaS PR - create a PR on the SaaS repo using the linked branch
        const branch = linkedBranches[repoId];
        if (!branch) {
          setError('Link a branch first before creating a SaaS PR');
          break;
        }
        updateStep(repoId, 4, 'running');
        try {
          const pr = await createSaasPr(branch);
          setLinkedPrs((prev) => ({ ...prev, [repoId]: pr.url }));
          updateStep(repoId, 4, 'success');
        } catch (err: any) {
          updateStep(repoId, 4, 'error');
          const msg = err?.response?.data?.message || 'Failed to create SaaS PR';
          setError(msg);
        }
        break;
      }
      case 5: {
        // Create OpenAM Branch - show approval modal for GitHub API calls
        const saasBranch = linkedBranches[repoId];
        if (!saasBranch) {
          setError('Create a SaaS branch first before creating an OpenAM branch');
          break;
        }
        updateStep(repoId, 5, 'running');
        try {
          const plan = await planOpenAmBranch(repoId, saasBranch);
          const initialResults: Record<string, 'pending' | 'executing' | 'success' | 'skipped' | 'error'> = {};
          plan.steps.forEach((s) => { initialResults[s.id] = s.skip ? 'skipped' : 'pending'; });
          setApprovalModal({
            repoId,
            jiraKey: saasBranch,
            plan,
            currentStep: plan.steps.findIndex((s) => !s.skip),
            stepResults: initialResults,
            stepIndex: 5,
          });
        } catch (err: any) {
          updateStep(repoId, 5, 'error');
          const msg = err?.response?.data?.message || 'Failed to plan OpenAM branch';
          setError(msg);
        }
        break;
      }
      case 6: {
        // Create OpenAM PR - create a PR on the OpenAM repo using the linked branch
        const openAmBranch = linkedOpenAmBranches[repoId];
        if (!openAmBranch) {
          setError('Link an OpenAM branch first before creating an OpenAM PR');
          break;
        }
        updateStep(repoId, 6, 'running');
        try {
          const pr = await createOpenAmPr(openAmBranch);
          setLinkedOpenAmPrs((prev) => ({ ...prev, [repoId]: pr.url }));
          updateStep(repoId, 6, 'success');
        } catch (err: any) {
          updateStep(repoId, 6, 'error');
          const msg = err?.response?.data?.message || 'Failed to create OpenAM PR';
          setError(msg);
        }
        break;
      }
      case 7: {
        // Run E2E - add the linked PR to the SaaS repo tracker then trigger rerun
        const prValue = linkedPrs[repoId];
        if (!prValue) {
          setError('Link a SaaS PR first before running E2E');
          break;
        }
        const prNum = /^\d+$/.test(prValue)
          ? parseInt(prValue, 10)
          : parseInt(prValue.match(/\/pull\/(\d+)/)?.[1] || '0', 10);
        if (!prNum) {
          setError('Could not determine PR number');
          break;
        }
        updateStep(repoId, 7, 'running');
        try {
          let repoData: RepoData;
          try {
            repoData = await addPr(prNum);
          } catch (addErr: any) {
            // If PR already tracked (409), fetch existing data
            if (addErr?.response?.status === 409) {
              repoData = await getRepoData();
            } else {
              throw addErr;
            }
          }
          const tracked = repoData.prs.find((p) => p.prNumber === prNum);
          if (tracked) {
            setTrackedPrIds((prev) => ({ ...prev, [repoId]: tracked.id }));
            await rerunE2e(tracked.id);
          }
          updateStep(repoId, 7, 'success');
        } catch (err: any) {
          updateStep(repoId, 7, 'error');
          const msg = err?.response?.data?.message || 'Failed to run E2E tests';
          setError(msg);
        }
        break;
      }
      case 8: {
        // Check E2E - fetch commit status for the tracked PR
        const trackedId = trackedPrIds[repoId];
        if (!trackedId) {
          setError('Run E2E first to track the PR');
          break;
        }
        updateStep(repoId, 8, 'running');
        try {
          const status = await getPrStatus(trackedId);
          if (status.state === 'success') {
            updateStep(repoId, 8, 'success');
          } else if (status.state === 'failure' || status.state === 'error') {
            updateStep(repoId, 8, 'error');
            setError(`E2E status: ${status.state}`);
          } else {
            updateStep(repoId, 8, 'idle');
            setError(`E2E status: ${status.state} (still pending)`);
          }
        } catch {
          updateStep(repoId, 8, 'error');
          setError('Failed to check E2E status');
        }
        break;
      }
      case 9: {
        // Rerun E2E
        const rerunId = trackedPrIds[repoId];
        if (!rerunId) {
          setError('Run E2E first to track the PR');
          break;
        }
        updateStep(repoId, 9, 'running');
        try {
          await rerunE2e(rerunId);
          updateStep(repoId, 9, 'success');
          // Reset check step so user can re-check
          updateStep(repoId, 8, 'idle');
        } catch {
          updateStep(repoId, 9, 'error');
          setError('Failed to rerun E2E tests');
        }
        break;
      }
      case 10: {
        // Create/Link Environment
        // If no env is linked, show the link input so user can paste a URL or create one
        if (envInputVisible[repoId]) {
          // Toggle off if already visible
          setEnvInputVisible((prev) => ({ ...prev, [repoId]: false }));
          break;
        }
        if (linkedEnvs[repoId]) {
          // Already linked — show input to re-link
          setEnvInputVisible((prev) => ({ ...prev, [repoId]: true }));
          break;
        }
        // Show link input; user can paste URL or click "create" to run the ForgeBlocks flow
        setEnvInputVisible((prev) => ({ ...prev, [repoId]: true }));
        break;
      }
      case 11:
        // Create Service Account - not yet implemented
        setError(`"${steps[stepIndex].label}" is not yet implemented`);
        break;
      case 12: {
        // Generate Journey - create test journey on the linked environment
        const envUrl = linkedEnvs[repoId];
        if (!envUrl) {
          setError('Link an environment first before generating a journey');
          break;
        }
        updateStep(repoId, 12, 'running');
        try {
          await generateTestJourney(envUrl, repoId);
          updateStep(repoId, 12, 'success');
        } catch (err: any) {
          updateStep(repoId, 12, 'error');
          const msg = err?.response?.data?.message || 'Failed to generate test journey';
          setError(msg);
        }
        break;
      }
      case 13: {
        // Test Journey - call ping-aic integration test
        updateStep(repoId, 13, 'running');
        try {
          await testJourney(repoId);
          updateStep(repoId, 13, 'success');
        } catch {
          // Always keep step as running — the agent may still be working
          updateStep(repoId, 13, 'running');
        }
        // Always fetch screenshots regardless of success/error
        try {
          const result = await getJourneyScreenshots(repoId);
          setJourneyScreenshots((prev) => ({ ...prev, [repoId]: result.screenshots }));
        } catch {
          // ignore
        }
        break;
      }
      case 14:
        // Request Approvals - not yet implemented
        setError(`"${steps[stepIndex].label}" is not yet implemented`);
        break;
    }
  };

  const handleLinkBranch = (repoId: string) => {
    const value = (branchInputValue[repoId] || '').trim();
    if (!value) return;

    setLinkedBranches((prev) => ({ ...prev, [repoId]: value }));
    setBranchInputVisible((prev) => ({ ...prev, [repoId]: false }));
    setBranchInputValue((prev) => ({ ...prev, [repoId]: '' }));
    updateStep(repoId, 3, 'success');
  };

  const handleLinkJira = async (repoId: string) => {
    const value = (jiraInputValue[repoId] || '').trim();
    if (!value) return;

    updateStep(repoId, 0, 'running');
    try {
      const ticket = await linkJiraTicket(repoId, value);
      setJiraTickets((prev) => ({ ...prev, [repoId]: ticket }));
      setJiraInputVisible((prev) => ({ ...prev, [repoId]: false }));
      setJiraInputValue((prev) => ({ ...prev, [repoId]: '' }));
      updateStep(repoId, 0, 'success');
    } catch (err: any) {
      updateStep(repoId, 0, 'error');
      const msg = err?.response?.data?.message || 'Failed to link JIRA ticket';
      setError(msg);
    }
  };

  const handleUnlinkJira = (repoId: string) => {
    setJiraTickets((prev) => {
      const next = { ...prev };
      delete next[repoId];
      return next;
    });
    setJiraInputVisible((prev) => ({ ...prev, [repoId]: false }));
    setJiraInputValue((prev) => ({ ...prev, [repoId]: '' }));
    updateStep(repoId, 0, 'idle');
  };

  const handleUnlinkBranch = (repoId: string) => {
    setLinkedBranches((prev) => {
      const next = { ...prev };
      delete next[repoId];
      return next;
    });
    updateStep(repoId, 3, 'idle');
  };

  const handleUnlinkOpenAmBranch = (repoId: string) => {
    setLinkedOpenAmBranches((prev) => {
      const next = { ...prev };
      delete next[repoId];
      return next;
    });
    updateStep(repoId, 5, 'idle');
  };

  const handleLinkPr = (repoId: string) => {
    const value = (prInputValue[repoId] || '').trim();
    if (!value) return;

    // Accept a full GitHub PR URL or just a PR number
    let prUrl = value;
    if (/^\d+$/.test(value)) {
      prUrl = value;
    } else {
      try {
        const parsed = new URL(value);
        if (parsed.hostname !== 'github.com' || !parsed.pathname.includes('/pull/')) {
          setError('Invalid PR URL. Expected: https://github.com/owner/repo/pull/123');
          return;
        }
      } catch {
        setError('Invalid PR URL. Expected: https://github.com/owner/repo/pull/123');
        return;
      }
    }

    setLinkedPrs((prev) => ({ ...prev, [repoId]: prUrl }));
    setPrInputVisible((prev) => ({ ...prev, [repoId]: false }));
    setPrInputValue((prev) => ({ ...prev, [repoId]: '' }));
    updateStep(repoId, 4, 'success');
  };

  const handleUnlinkPr = (repoId: string) => {
    setLinkedPrs((prev) => {
      const next = { ...prev };
      delete next[repoId];
      return next;
    });
    updateStep(repoId, 4, 'idle');
    // Also reset Create OpenAM PR step
    updateStep(repoId, 6, 'idle');
  };

  const formatPrDisplay = (prValue: string): { label: string; url: string | null } => {
    if (/^\d+$/.test(prValue)) {
      return { label: `PR #${prValue}`, url: null };
    }
    const match = prValue.match(/\/pull\/(\d+)/);
    return { label: match ? `PR #${match[1]}` : 'Linked PR', url: prValue };
  };

  const handleUnlinkEnv = (repoId: string) => {
    setLinkedEnvs((prev) => {
      const next = { ...prev };
      delete next[repoId];
      return next;
    });
    updateStep(repoId, 10, 'idle');
  };

  const handleLinkEnv = (repoId: string) => {
    const value = (envInputValue[repoId] || '').trim();
    if (!value) return;
    setLinkedEnvs((prev) => ({ ...prev, [repoId]: value }));
    setEnvInputVisible((prev) => ({ ...prev, [repoId]: false }));
    setEnvInputValue((prev) => ({ ...prev, [repoId]: '' }));
    updateStep(repoId, 10, 'success');
  };

  const handleCreateEnv = async (repoId: string) => {
    const saasBranchForEnv = linkedBranches[repoId];
    if (!saasBranchForEnv) {
      setError('Create a SaaS branch first before creating an environment');
      return;
    }
    setEnvInputVisible((prev) => ({ ...prev, [repoId]: false }));
    updateStep(repoId, 10, 'running');
    try {
      const plan = await planEnvironment(saasBranchForEnv);
      const initialResults: Record<string, 'pending' | 'executing' | 'success' | 'skipped' | 'error'> = {};
      plan.steps.forEach((s) => { initialResults[s.id] = s.skip ? 'skipped' : 'pending'; });
      setApprovalModal({
        repoId,
        jiraKey: saasBranchForEnv,
        plan,
        currentStep: plan.steps.findIndex((s) => !s.skip),
        stepResults: initialResults,
        stepIndex: 10,
      });
    } catch (err: any) {
      updateStep(repoId, 10, 'error');
      const msg = err?.response?.data?.message || 'Failed to plan environment creation';
      setError(msg);
    }
  };

  const displayName = (url: string) => {
    const match = url.match(/github\.com\/[^/]+\/([^/]+)/);
    return match ? match[1].replace(/\.git$/, '') : url;
  };

  const displayOwnerName = (url: string) => {
    const match = url.match(/github\.com\/([^/]+\/[^/]+)/);
    return match ? match[1].replace(/\.git$/, '') : url;
  };

  const handleApproveStep = async (stepId: string) => {
    if (!approvalModal) return;
    const { repoId, jiraKey, plan, stepIndex } = approvalModal;

    setApprovalModal((prev) => prev ? {
      ...prev,
      stepResults: { ...prev.stepResults, [stepId]: 'executing' },
      error: undefined,
    } : null);

    try {
      const result = stepIndex === 10
        ? await executeEnvironmentStep(jiraKey, stepId)
        : stepIndex === 5
          ? await executeOpenAmBranchStep(repoId, jiraKey, stepId)
          : await executeSaasBranchStep(repoId, jiraKey, stepId);
      const status = result.skipped ? 'skipped' : 'success';

      setApprovalModal((prev) => {
        if (!prev) return null;
        const newResults = { ...prev.stepResults, [stepId]: status as 'success' | 'skipped' };
        const nextIdx = plan.steps.findIndex((s, i) => i > prev.currentStep && !s.skip && newResults[s.id] === 'pending');
        const allDone = plan.steps.every((s) => newResults[s.id] !== 'pending');

        if (allDone) {
          if (stepIndex === 10) {
            setLinkedEnvs((b) => ({ ...b, [repoId]: `tap-${plan.branchName}` }));
          } else if (stepIndex === 5) {
            setLinkedOpenAmBranches((b) => ({ ...b, [repoId]: plan.branchName }));
          } else {
            setLinkedBranches((b) => ({ ...b, [repoId]: plan.branchName }));
          }
          updateStep(repoId, stepIndex, 'success');
          return null;
        }

        return { ...prev, stepResults: newResults, currentStep: nextIdx >= 0 ? nextIdx : prev.currentStep };
      });
    } catch (err: any) {
      const msg = err?.response?.data?.message || `Failed to execute step: ${stepId}`;
      setApprovalModal((prev) => prev ? {
        ...prev,
        stepResults: { ...prev.stepResults, [stepId]: 'error' },
        error: msg,
      } : null);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 0' }}>
        <span style={{ color: '#9ca3af', fontSize: '0.8125rem' }}>Loading...</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Error banner */}
      {error && (
        <div
          style={{
            padding: '0.625rem 1rem',
            backgroundColor: '#1a1f2e',
            border: '1px solid #374151',
            borderRadius: '0.5rem',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ color: '#f87171', fontSize: '0.8125rem', fontWeight: 500 }}>{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '1rem', lineHeight: 1, padding: '0 0.25rem' }}
          >
            ×
          </button>
        </div>
      )}

      <div
        style={{
          backgroundColor: '#1a1f2e',
          borderRadius: '0.75rem',
          border: '1px solid #2d3548',
          boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '0.75rem 1.25rem',
            background: 'linear-gradient(135deg, #1e3a5f 0%, #1a1f2e 100%)',
            borderBottom: '1px solid #2d3548',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1rem' }}>⚙</span>
            <span
              style={{
                fontSize: '0.75rem',
                fontWeight: 700,
                color: '#e2e8f0',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              Existing Plugins Pipeline
            </span>
          </div>
          <form onSubmit={handleAdd} style={{ display: 'flex', flexDirection: 'row', gap: '0.375rem', alignItems: 'center' }}>
            <input
              type="url"
              placeholder="https://github.com/owner/repo"
              value={urlInput}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUrlInput(e.target.value)}
              style={{
                padding: '0.375rem 0.75rem',
                border: '1px solid #374151',
                borderRadius: '0.375rem',
                fontSize: '0.75rem',
                width: '260px',
                outline: 'none',
                backgroundColor: '#111827',
                color: '#e2e8f0',
                boxSizing: 'border-box' as const,
              }}
              required
            />
            <button
              type="submit"
              disabled={adding}
              style={{
                border: '1px solid #3b82f6',
                borderRadius: '0.375rem',
                padding: '0.375rem 0.75rem',
                fontSize: '0.6875rem',
                fontWeight: 600,
                cursor: 'pointer',
                backgroundColor: 'rgba(59,130,246,0.15)',
                color: '#60a5fa',
                opacity: adding ? 0.5 : 1,
                whiteSpace: 'nowrap' as const,
              }}
            >
              {adding ? '...' : '+ Add'}
            </button>
          </form>
        </div>

        {/* Repo list */}
        {data.repos.length === 0 ? (
          <div style={{ padding: '3rem 1rem', textAlign: 'center' }}>
            <div style={{ color: '#4b5563', fontSize: '1.5rem', marginBottom: '0.5rem' }}>⚙</div>
            <span style={{ color: '#6b7280', fontSize: '0.8125rem' }}>
              No repositories tracked yet
            </span>
          </div>
        ) : (
          <div>
            {data.repos.map((repo, idx) => {
              const versionInfo = versions[repo.id];
              const isLoading = versionLoading[repo.id];
              const isExpanded = expandedRepos[repo.id];
              const steps = getSteps(repo.id);

              return (
                <div key={repo.id}>
                  {/* Repo row */}
                  <div
                    style={{
                      borderTop: idx > 0 ? '1px solid #2d3548' : 'none',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      padding: '0.75rem 1rem',
                      gap: '0.75rem',
                      cursor: 'pointer',
                      backgroundColor: isExpanded ? '#111827' : 'transparent',
                      transition: 'background-color 0.15s',
                    }}
                    onClick={() => setExpandedRepos((prev) => ({ ...prev, [repo.id]: !prev[repo.id] }))}
                  >
                    {/* Expand indicator */}
                    <span
                      style={{
                        color: '#6b7280',
                        fontSize: '0.625rem',
                        transition: 'transform 0.2s',
                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        flexShrink: 0,
                        width: '0.75rem',
                        textAlign: 'center',
                      }}
                    >
                      ▶
                    </span>

                    {/* Repo info */}
                    <div style={{ minWidth: '10rem', flexShrink: 0 }}>
                      <a
                        href={repo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ fontWeight: 700, color: '#e2e8f0', fontSize: '0.8125rem', textDecoration: 'none', display: 'block', lineHeight: 1.3 }}
                      >
                        {displayName(repo.url)}
                      </a>
                      <span style={{ fontSize: '0.625rem', color: '#6b7280', fontFamily: "'SF Mono', 'Fira Code', monospace" }}>
                        {displayOwnerName(repo.url)}
                      </span>
                    </div>

                    {/* Version info */}
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '0.375rem', alignItems: 'center', flex: 1, minWidth: 0 }}>
                      {isLoading ? (
                        <span style={{ fontSize: '0.625rem', color: '#6b7280' }}>loading...</span>
                      ) : versionInfo?.version ? (
                        <>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '0.125rem 0.5rem',
                              borderRadius: '9999px',
                              backgroundColor: 'rgba(59,130,246,0.15)',
                              fontSize: '0.625rem',
                              fontWeight: 600,
                              color: '#60a5fa',
                              fontFamily: "'SF Mono', 'Fira Code', monospace",
                              border: '1px solid rgba(59,130,246,0.3)',
                            }}
                            title="Plugin repo version"
                          >
                            v{versionInfo.version}
                          </span>
                          {versionInfo.dockerfileVersion && (() => {
                            const aligned = versionInfo.version === versionInfo.dockerfileVersion;
                            const badgeColor = aligned
                              ? { bg: 'rgba(16,185,129,0.15)', text: '#34d399', border: 'rgba(16,185,129,0.3)' }
                              : { bg: 'rgba(239,68,68,0.15)', text: '#f87171', border: 'rgba(239,68,68,0.3)' };
                            return (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '0.125rem 0.5rem',
                                  borderRadius: '9999px',
                                  backgroundColor: badgeColor.bg,
                                  fontSize: '0.625rem',
                                  fontWeight: 600,
                                  color: badgeColor.text,
                                  fontFamily: "'SF Mono', 'Fira Code', monospace",
                                  border: `1px solid ${badgeColor.border}`,
                                }}
                                title={`SaaS Dockerfile version (${versionInfo.dockerfileEnvKey}_VERSION)${aligned ? ' — aligned' : ' — mismatched'}`}
                              >
                                SaaS: v{versionInfo.dockerfileVersion}
                              </span>
                            );
                          })()}
                        </>
                      ) : null}

                      {/* Pipeline mini-indicator */}
                      <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center', marginLeft: 'auto' }}>
                        {steps.map((step, i) => (
                          <div
                            key={i}
                            style={{
                              width: '0.5rem',
                              height: '0.5rem',
                              borderRadius: '50%',
                              backgroundColor: STEP_COLORS[step.status].border,
                              transition: 'background-color 0.3s',
                            }}
                            title={`${step.label}: ${step.status}`}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '0.25rem', flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); fetchVersion(repo.id); }}
                        disabled={isLoading}
                        style={{
                          border: '1px solid #374151',
                          borderRadius: '0.375rem',
                          padding: '0.25rem 0.375rem',
                          fontSize: '0.6875rem',
                          cursor: 'pointer',
                          backgroundColor: 'transparent',
                          color: '#9ca3af',
                          opacity: isLoading ? 0.4 : 1,
                          lineHeight: 1,
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                        title="Refresh versions"
                      >
                        ↻
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleResetPipeline(repo.id); }}
                        style={{
                          border: '1px solid #92400e',
                          borderRadius: '0.375rem',
                          padding: '0.25rem 0.375rem',
                          fontSize: '0.6875rem',
                          cursor: 'pointer',
                          backgroundColor: 'transparent',
                          color: '#fbbf24',
                          lineHeight: 1,
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                        title="Reset pipeline"
                      >
                        ⟲
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleRemove(repo.id); }}
                        style={{
                          border: '1px solid #7f1d1d',
                          borderRadius: '0.375rem',
                          padding: '0.25rem 0.375rem',
                          fontSize: '0.6875rem',
                          cursor: 'pointer',
                          backgroundColor: 'transparent',
                          color: '#f87171',
                          lineHeight: 1,
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                        title="Remove repository"
                      >
                        ×
                      </button>
                    </div>
                  </div>

                  {/* Pipeline steps (expanded) */}
                  {isExpanded && (
                    <div
                      style={{
                        backgroundColor: '#111827',
                        borderTop: '1px solid #1e293b',
                        padding: '1.5rem 1.25rem 1.75rem',
                      }}
                    >
                      {/* Pipeline visualization */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'flex-start',
                          gap: '0',
                          position: 'relative',
                          overflowX: 'auto',
                          paddingBottom: '0.5rem',
                        }}
                      >
                        {steps.map((step, i) => {
                          const colors = STEP_COLORS[step.status];
                          const isClickable = step.status !== 'running';
                          const isLinkBranch = i === 3;
                          const branchLinked = linkedBranches[repo.id];
                          const isCreatePr = i === 4;
                          const prLinked = linkedPrs[repo.id];
                          const prDisplay = prLinked ? formatPrDisplay(prLinked) : null;
                          const isLinkOpenAmBranch = i === 5;
                          const openAmBranchLinked = linkedOpenAmBranches[repo.id];
                          const isCreateOpenAmPr = i === 6;
                          const openAmPrLinked = linkedOpenAmPrs[repo.id];
                          const openAmPrDisplay = openAmPrLinked ? formatPrDisplay(openAmPrLinked) : null;
                          const isLinkEnv = i === 10;
                          const envLinked = linkedEnvs[repo.id];
                          const isJiraStep = i === 0;
                          const jiraTicket = jiraTickets[repo.id];
                          const isJenkinsStep = i === 1;
                          const jenkinsBuild = jenkinsBuilds[repo.id];
                          const isTestJourney = i === 13;
                          const screenshots = journeyScreenshots[repo.id];
                          const testJourneyHasScreenshots = isTestJourney && screenshots && screenshots.length > 0;
                          const testJourneyEffectiveStatus = testJourneyHasScreenshots && step.status === 'idle' ? 'success' : step.status;
                          const effectiveColors = isTestJourney ? STEP_COLORS[testJourneyEffectiveStatus] : colors;
                          const effectiveIcon = isTestJourney ? STEP_ICONS[testJourneyEffectiveStatus] : STEP_ICONS[step.status];

                          return (
                            <div
                              key={i}
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                              }}
                            >
                              {/* Connector line */}
                              {i > 0 && (
                                <div
                                  style={{
                                    width: '1.5rem',
                                    height: '2px',
                                    backgroundColor: steps[i - 1].status === 'success' ? '#10b981' : '#374151',
                                    transition: 'background-color 0.3s',
                                    marginTop: '1.125rem',
                                    flexShrink: 0,
                                  }}
                                />
                              )}

                              {/* Step node */}
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: '0.375rem',
                                  cursor: isClickable ? 'pointer' : 'default',
                                  minWidth: '4.25rem',
                                  flexShrink: 0,
                                }}
                                onClick={() => isClickable && handleStepClick(repo.id, i)}
                              >
                                <div
                                  style={{
                                    width: '2.25rem',
                                    height: '2.25rem',
                                    borderRadius: '50%',
                                    backgroundColor: effectiveColors.bg,
                                    border: `2px solid ${effectiveColors.border}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    color: effectiveColors.text,
                                    boxShadow: effectiveColors.glow,
                                    transition: 'all 0.3s',
                                    animation: step.status === 'running' ? 'pulse 1.5s infinite' : 'none',
                                  }}
                                >
                                  {isLinkBranch && branchLinked ? '🌿' : isCreatePr && prLinked ? '🔗' : isLinkOpenAmBranch && openAmBranchLinked ? '🌿' : isCreateOpenAmPr && openAmPrLinked ? '🔗' : isLinkEnv && envLinked ? '🌐' : isJiraStep && jiraTicket ? '🎫' : isJenkinsStep && jenkinsBuild ? '🔨' : effectiveIcon}
                                </div>
                                <span
                                  style={{
                                    fontSize: '0.5rem',
                                    color: (isTestJourney ? testJourneyEffectiveStatus : step.status) === 'idle' ? '#6b7280' : effectiveColors.text,
                                    fontWeight: (isTestJourney ? testJourneyEffectiveStatus : step.status) === 'idle' ? 400 : 600,
                                    textAlign: 'center',
                                    maxWidth: '4.25rem',
                                    lineHeight: 1.2,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {step.label}
                                </span>

                                {/* JIRA ticket info below the Get JIRA Ticket step */}
                                {isJiraStep && jiraTicket && (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', marginTop: '0.125rem' }}>
                                    <a
                                      href={jiraTicket.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        fontSize: '0.5625rem',
                                        color: '#a78bfa',
                                        textDecoration: 'none',
                                        fontWeight: 600,
                                        fontFamily: "'SF Mono', 'Fira Code', monospace",
                                        padding: '0.125rem 0.375rem',
                                        backgroundColor: 'rgba(167,139,250,0.1)',
                                        borderRadius: '0.25rem',
                                        border: '1px solid rgba(167,139,250,0.25)',
                                        maxWidth: '6rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                      title={jiraTicket.summary}
                                    >
                                      {jiraTicket.key} ↗
                                    </a>
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); handleUnlinkJira(repo.id); }}
                                        style={{
                                          fontSize: '0.5rem',
                                          color: '#6b7280',
                                          background: 'none',
                                          border: 'none',
                                          cursor: 'pointer',
                                          textDecoration: 'underline',
                                          padding: 0,
                                        }}
                                      >
                                        unlink
                                      </button>
                                    </div>
                                  )}

                                  {isJiraStep && !jiraTicket && jiraInputVisible[repo.id] && (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', marginTop: '0.25rem' }} onClick={(e) => e.stopPropagation()}>
                                      <input
                                        type="text"
                                        placeholder="JIRA key or URL"
                                        value={jiraInputValue[repo.id] || ''}
                                        onChange={(e) => setJiraInputValue((prev) => ({ ...prev, [repo.id]: e.target.value }))}
                                        onKeyDown={(e) => { if (e.key === 'Enter') void handleLinkJira(repo.id); }}
                                        style={{
                                          fontSize: '0.5rem',
                                          padding: '0.125rem 0.25rem',
                                          borderRadius: '0.25rem',
                                          border: '1px solid #374151',
                                          backgroundColor: '#1f2937',
                                          color: '#e5e7eb',
                                          width: '6rem',
                                          outline: 'none',
                                        }}
                                      />
                                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                                        <button
                                          type="button"
                                          onClick={() => void handleLinkJira(repo.id)}
                                          style={{
                                            fontSize: '0.5rem',
                                            color: '#a78bfa',
                                            background: 'none',
                                            border: '1px solid rgba(167,139,250,0.4)',
                                            borderRadius: '0.25rem',
                                            cursor: 'pointer',
                                            padding: '0.125rem 0.375rem',
                                          }}
                                        >
                                          link
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setJiraInputVisible((prev) => ({ ...prev, [repo.id]: false }));
                                            setJiraInputValue((prev) => ({ ...prev, [repo.id]: '' }));
                                          }}
                                          style={{
                                            fontSize: '0.5rem',
                                            color: '#9ca3af',
                                            background: 'none',
                                            border: '1px solid rgba(156,163,175,0.35)',
                                            borderRadius: '0.25rem',
                                            cursor: 'pointer',
                                            padding: '0.125rem 0.375rem',
                                          }}
                                        >
                                          cancel
                                        </button>
                                      </div>
                                    </div>
                                  )}

                                  {isJiraStep && !jiraTicket && !jiraInputVisible[repo.id] && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setJiraInputVisible((prev) => ({ ...prev, [repo.id]: true }));
                                      }}
                                      style={{
                                        fontSize: '0.5rem',
                                        color: '#a78bfa',
                                        background: 'none',
                                        border: '1px solid rgba(167,139,250,0.35)',
                                        borderRadius: '0.25rem',
                                        cursor: 'pointer',
                                        padding: '0.125rem 0.375rem',
                                        marginTop: '0.25rem',
                                      }}
                                    >
                                      link ticket
                                    </button>
                                  )}

                                {/* Jenkins build info below the Run Jenkins Pipeline step */}
                                {isJenkinsStep && jenkinsBuild && (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', marginTop: '0.125rem' }}>
                                    <a
                                      href={jenkinsBuild.jobUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        fontSize: '0.5625rem',
                                        color: '#fbbf24',
                                        textDecoration: 'none',
                                        fontWeight: 600,
                                        fontFamily: "'SF Mono', 'Fira Code', monospace",
                                        padding: '0.125rem 0.375rem',
                                        backgroundColor: 'rgba(251,191,36,0.1)',
                                        borderRadius: '0.25rem',
                                        border: '1px solid rgba(251,191,36,0.25)',
                                        maxWidth: '6rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                      title={jenkinsBuild.jobName}
                                    >
                                      {jenkinsBuild.jobName} ↗
                                    </a>
                                  </div>
                                )}

                                {/* Created branch info below the Create SaaS Branch step */}
                                {isLinkBranch && branchLinked && (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', marginTop: '0.125rem' }}>
                                    <span
                                      style={{
                                        fontSize: '0.5625rem',
                                        color: '#34d399',
                                        fontWeight: 600,
                                        fontFamily: "'SF Mono', 'Fira Code', monospace",
                                        padding: '0.125rem 0.375rem',
                                        backgroundColor: 'rgba(16,185,129,0.1)',
                                        borderRadius: '0.25rem',
                                        border: '1px solid rgba(16,185,129,0.25)',
                                        maxWidth: '6rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                      title={branchLinked}
                                    >
                                      {branchLinked}
                                    </span>
                                  </div>
                                )}

                                {/* Created PR info below the Create SaaS PR step */}
                                {isCreatePr && prLinked && prDisplay && (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', marginTop: '0.125rem' }}>
                                    {prDisplay.url ? (
                                      <a
                                        href={prDisplay.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                          fontSize: '0.625rem',
                                          color: '#60a5fa',
                                          textDecoration: 'none',
                                          fontWeight: 600,
                                          fontFamily: "'SF Mono', 'Fira Code', monospace",
                                          padding: '0.125rem 0.375rem',
                                          backgroundColor: 'rgba(59,130,246,0.1)',
                                          borderRadius: '0.25rem',
                                          border: '1px solid rgba(59,130,246,0.25)',
                                        }}
                                      >
                                        {prDisplay.label} ↗
                                      </a>
                                    ) : (
                                      <span
                                        style={{
                                          fontSize: '0.625rem',
                                          color: '#60a5fa',
                                          fontWeight: 600,
                                          fontFamily: "'SF Mono', 'Fira Code', monospace",
                                          padding: '0.125rem 0.375rem',
                                          backgroundColor: 'rgba(59,130,246,0.1)',
                                          borderRadius: '0.25rem',
                                          border: '1px solid rgba(59,130,246,0.25)',
                                        }}
                                      >
                                        {prDisplay.label}
                                      </span>
                                    )}
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); handleUnlinkPr(repo.id); }}
                                      style={{
                                        fontSize: '0.5rem',
                                        color: '#6b7280',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        padding: 0,
                                      }}
                                    >
                                      unlink
                                    </button>
                                    <span
                                      style={{
                                        fontSize: '0.5rem',
                                        color: '#c4b5fd',
                                        textAlign: 'center',
                                        maxWidth: '6rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                      title={jiraTicket.summary}
                                    >
                                      {jiraTicket.summary || jiraTicket.status}
                                    </span>
                                  </div>
                                )}

                                {/* Linked OpenAM branch info below the Link OpenAM Branch step */}
                                {isLinkOpenAmBranch && openAmBranchLinked && (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', marginTop: '0.125rem' }}>
                                    <span
                                      style={{
                                        fontSize: '0.5625rem',
                                        color: '#f59e0b',
                                        fontWeight: 600,
                                        fontFamily: "'SF Mono', 'Fira Code', monospace",
                                        padding: '0.125rem 0.375rem',
                                        backgroundColor: 'rgba(245,158,11,0.1)',
                                        borderRadius: '0.25rem',
                                        border: '1px solid rgba(245,158,11,0.25)',
                                        maxWidth: '6rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                      title={openAmBranchLinked}
                                    >
                                      {openAmBranchLinked}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); handleUnlinkOpenAmBranch(repo.id); }}
                                      style={{
                                        fontSize: '0.5rem',
                                        color: '#6b7280',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        padding: 0,
                                      }}
                                    >
                                      unlink
                                    </button>
                                  </div>
                                )}

                                {/* Created OpenAM PR info below the Create OpenAM PR step */}
                                {isCreateOpenAmPr && openAmPrLinked && openAmPrDisplay && (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', marginTop: '0.125rem' }}>
                                    {openAmPrDisplay.url ? (
                                      <a
                                        href={openAmPrDisplay.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                          fontSize: '0.625rem',
                                          color: '#f59e0b',
                                          textDecoration: 'none',
                                          fontWeight: 600,
                                          fontFamily: "'SF Mono', 'Fira Code', monospace",
                                          padding: '0.125rem 0.375rem',
                                          backgroundColor: 'rgba(245,158,11,0.1)',
                                          borderRadius: '0.25rem',
                                          border: '1px solid rgba(245,158,11,0.25)',
                                        }}
                                      >
                                        {openAmPrDisplay.label} ↗
                                      </a>
                                    ) : (
                                      <span
                                        style={{
                                          fontSize: '0.625rem',
                                          color: '#f59e0b',
                                          fontWeight: 600,
                                          fontFamily: "'SF Mono', 'Fira Code', monospace",
                                          padding: '0.125rem 0.375rem',
                                          backgroundColor: 'rgba(245,158,11,0.1)',
                                          borderRadius: '0.25rem',
                                          border: '1px solid rgba(245,158,11,0.25)',
                                        }}
                                      >
                                        {openAmPrDisplay.label}
                                      </span>
                                    )}
                                  </div>
                                )}

                                {/* Linked Environment info below the Link Environment step */}
                                {isLinkEnv && envLinked && (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', marginTop: '0.125rem' }}>
                                    <a
                                      href={envLinked}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        fontSize: '0.625rem',
                                        color: '#34d399',
                                        textDecoration: 'none',
                                        fontWeight: 600,
                                        fontFamily: "'SF Mono', 'Fira Code', monospace",
                                        padding: '0.125rem 0.375rem',
                                        backgroundColor: 'rgba(16,185,129,0.1)',
                                        borderRadius: '0.25rem',
                                        border: '1px solid rgba(16,185,129,0.25)',
                                        maxWidth: '6rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                      title={envLinked}
                                    >
                                      Environment ↗
                                    </a>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); handleUnlinkEnv(repo.id); }}
                                      style={{
                                        fontSize: '0.5rem',
                                        color: '#6b7280',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        padding: 0,
                                      }}
                                    >
                                      unlink
                                    </button>
                                  </div>
                                )}

                                {/* Link/Create Environment input */}
                                {isLinkEnv && !envLinked && envInputVisible[repo.id] && (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', marginTop: '0.25rem' }} onClick={(e) => e.stopPropagation()}>
                                    <input
                                      type="text"
                                      placeholder="Paste env URL..."
                                      value={envInputValue[repo.id] || ''}
                                      onChange={(e) => setEnvInputValue((prev) => ({ ...prev, [repo.id]: e.target.value }))}
                                      onKeyDown={(e) => { if (e.key === 'Enter') handleLinkEnv(repo.id); }}
                                      style={{
                                        fontSize: '0.5rem',
                                        padding: '0.125rem 0.25rem',
                                        borderRadius: '0.25rem',
                                        border: '1px solid #374151',
                                        backgroundColor: '#1f2937',
                                        color: '#e5e7eb',
                                        width: '6rem',
                                        outline: 'none',
                                      }}
                                    />
                                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                                      <button
                                        type="button"
                                        onClick={() => handleLinkEnv(repo.id)}
                                        style={{
                                          fontSize: '0.5rem',
                                          color: '#34d399',
                                          background: 'none',
                                          border: '1px solid rgba(16,185,129,0.4)',
                                          borderRadius: '0.25rem',
                                          cursor: 'pointer',
                                          padding: '0.125rem 0.375rem',
                                        }}
                                      >
                                        link
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleCreateEnv(repo.id)}
                                        style={{
                                          fontSize: '0.5rem',
                                          color: '#60a5fa',
                                          background: 'none',
                                          border: '1px solid rgba(59,130,246,0.4)',
                                          borderRadius: '0.25rem',
                                          cursor: 'pointer',
                                          padding: '0.125rem 0.375rem',
                                        }}
                                      >
                                        create
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {/* Screenshots below Test Journey step */}
                                {isTestJourney && screenshots && screenshots.length > 0 && (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', marginTop: '0.25rem' }}>
                                    <span style={{ fontSize: '0.5rem', color: '#9ca3af' }}>
                                      {screenshots.length} screenshot{screenshots.length !== 1 ? 's' : ''}
                                    </span>
                                    <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', justifyContent: 'center', maxWidth: '8rem' }}>
                                      {screenshots.map((ss, idx) => (
                                        <div
                                          key={ss.filename}
                                          onClick={(e) => { e.stopPropagation(); setScreenshotModal({ repoId: repo.id, index: idx }); }}
                                          style={{ cursor: 'pointer', position: 'relative' }}
                                          title={`${ss.filename}\n${new Date(ss.timestamp).toLocaleString()}`}
                                        >
                                          <img
                                            src={ss.dataUrl}
                                            alt={ss.filename}
                                            style={{
                                              width: '3rem',
                                              height: '2rem',
                                              objectFit: 'cover',
                                              borderRadius: '0.25rem',
                                              border: '1px solid #374151',
                                            }}
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* CSS animation for running state */}
                      <style>{`
                        @keyframes pulse {
                          0%, 100% { opacity: 1; }
                          50% { opacity: 0.5; }
                        }
                      `}</style>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Approval modal for Create SaaS Branch */}
      {approvalModal && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
          }}
          onClick={() => {
            setApprovalModal(null);
            updateStep(approvalModal.repoId, approvalModal.stepIndex, 'pending');
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#1e1e2e',
              borderRadius: '0.75rem',
              border: '1px solid #374151',
              padding: '1.5rem',
              maxWidth: '700px',
              width: '100%',
              maxHeight: '85vh',
              overflowY: 'auto',
              color: '#e2e8f0',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
                {approvalModal.stepIndex === 10 ? 'Create Environment' : approvalModal.stepIndex === 5 ? 'Create OpenAM Branch' : 'Create SaaS Branch'}: {approvalModal.stepIndex === 10 && 'envName' in approvalModal.plan ? (approvalModal.plan as EnvironmentPlan).envName : approvalModal.plan.branchName}
              </h3>
              <button
                type="button"
                onClick={() => { setApprovalModal(null); updateStep(approvalModal.repoId, approvalModal.stepIndex, 'pending'); }}
                style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '1.25rem' }}
              >
                ✕
              </button>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '1rem' }}>
              Version: {approvalModal.plan.pomVersion} · Plugin: {approvalModal.plan.pluginName}
            </div>

            {approvalModal.error && (
              <div style={{ background: '#7f1d1d', border: '1px solid #dc2626', borderRadius: '0.375rem', padding: '0.5rem 0.75rem', marginBottom: '1rem', fontSize: '0.75rem' }}>
                {approvalModal.error}
              </div>
            )}

            {approvalModal.plan.steps.map((step, i) => {
              const status = approvalModal.stepResults[step.id];
              const isCurrent = i === approvalModal.currentStep;
              return (
                <div
                  key={step.id}
                  style={{
                    border: `1px solid ${isCurrent ? '#3b82f6' : '#374151'}`,
                    borderRadius: '0.5rem',
                    padding: '0.75rem',
                    marginBottom: '0.75rem',
                    opacity: status === 'success' || status === 'skipped' ? 0.6 : 1,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                      {status === 'success' ? '✓ ' : status === 'skipped' ? '⏭ ' : status === 'error' ? '✗ ' : status === 'executing' ? '⏳ ' : ''}{step.description}
                    </div>
                    {isCurrent && status === 'pending' && (
                      <button
                        type="button"
                        onClick={() => handleApproveStep(step.id)}
                        style={{
                          background: '#2563eb',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '0.375rem',
                          padding: '0.375rem 0.75rem',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Approve & Execute
                      </button>
                    )}
                    {status === 'executing' && (
                      <span style={{ fontSize: '0.75rem', color: '#facc15' }}>Executing...</span>
                    )}
                    {step.skip && (
                      <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{step.skipReason}</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.6875rem', fontFamily: 'monospace', color: '#9ca3af' }}>
                    <span style={{ color: step.method === 'POST' ? '#34d399' : '#60a5fa', fontWeight: 600 }}>{step.method}</span>{' '}
                    {step.url}
                  </div>
                  {!step.skip && Object.keys(step.body).length > 0 && (
                    <pre style={{
                      background: '#111827',
                      borderRadius: '0.375rem',
                      padding: '0.5rem',
                      marginTop: '0.5rem',
                      fontSize: '0.625rem',
                      overflow: 'auto',
                      maxHeight: '200px',
                      color: '#d1d5db',
                      margin: '0.5rem 0 0 0',
                    }}>
                      {JSON.stringify(step.body, null, 2)}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Screenshot modal */}
      {screenshotModal && (() => {
        const ss = journeyScreenshots[screenshotModal.repoId];
        if (!ss || ss.length === 0) return null;
        const current = ss[screenshotModal.index];
        if (!current) return null;
        return (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.85)',
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2rem',
            }}
            onClick={() => setScreenshotModal(null)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setScreenshotModal((prev) => prev ? { ...prev, index: Math.max(0, prev.index - 1) } : null); }}
                disabled={screenshotModal.index === 0}
                style={{
                  background: 'none',
                  border: '1px solid #4b5563',
                  borderRadius: '0.375rem',
                  color: screenshotModal.index === 0 ? '#4b5563' : '#e2e8f0',
                  cursor: screenshotModal.index === 0 ? 'default' : 'pointer',
                  padding: '0.375rem 0.75rem',
                  fontSize: '1rem',
                }}
              >
                ←
              </button>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#e2e8f0', fontSize: '0.875rem', fontWeight: 600 }}>
                  {current.filename}
                </div>
                <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                  {new Date(current.timestamp).toLocaleString()} · {screenshotModal.index + 1} / {ss.length}
                </div>
              </div>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setScreenshotModal((prev) => prev ? { ...prev, index: Math.min(ss.length - 1, prev.index + 1) } : null); }}
                disabled={screenshotModal.index === ss.length - 1}
                style={{
                  background: 'none',
                  border: '1px solid #4b5563',
                  borderRadius: '0.375rem',
                  color: screenshotModal.index === ss.length - 1 ? '#4b5563' : '#e2e8f0',
                  cursor: screenshotModal.index === ss.length - 1 ? 'default' : 'pointer',
                  padding: '0.375rem 0.75rem',
                  fontSize: '1rem',
                }}
              >
                →
              </button>
            </div>
            <img
              src={current.dataUrl}
              alt={current.filename}
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '90vw',
                maxHeight: '75vh',
                objectFit: 'contain',
                borderRadius: '0.5rem',
                border: '1px solid #374151',
              }}
            />
            <button
              type="button"
              onClick={() => setScreenshotModal(null)}
              style={{
                marginTop: '1rem',
                background: 'none',
                border: '1px solid #4b5563',
                borderRadius: '0.375rem',
                color: '#e2e8f0',
                cursor: 'pointer',
                padding: '0.375rem 1rem',
                fontSize: '0.75rem',
              }}
            >
              Close
            </button>
          </div>
        );
      })()}
    </div>
  );
};

export default RepoVersions;
